import {useEffect, useState} from "react";
import appConstants from '../../../../constants';
import fetchUserCountry from '../../../../services/fetchUserCountry';
import getGoogleGeoLocation from '../../../../services/getGoogleGeoLocation';


export default function useUserGeoLocation(
  areFiltersLoaded,
  filterLoadingError,
  countryFilterList,
  selectCountryFilterValue,
  isCountryAlreadySelected
) {
  const [isUserGeoLocationInProgress, setIsUserGeoLocationInProgress] = useState(false);
  const [isUserGeoLocationCompleted, setIsUserGeoLocationCompleted] = useState(false);

  const [isGoogleGeoLocationCompleted, setIsGoogleGeoLocationCompleted] = useState(false);
  const [googleGeoLocation, setGoogleGeoLocation] = useState(null);
  const [isGoogleGeoLocationInitiated, setIsGoogleGeoLocationInitiated] = useState(false);
  const [isGoogleGeoLocationInProgress, setIsGoogleGeoLocationInProgress] = useState(false);
  const [googleGeoLocationError, setGoogleGeoLocationError] = useState(null);

  const [isNavigatorGeoLocationCompleted, setIsNavigatorGeoLocationCompleted] = useState(false);
  const [navigatorGeoLocation, setNavigatorGeoLocation] = useState(null);
  const [isNavigatorGeoLocationRequested, setIsNavigatorGeoLocationRequested] = useState(false);
  const [isNavigatorGeoLocationInitiated, setIsNavigatorGeoLocationInitiated] = useState(false);
  const [isNavigatorGeoLocationInProgress, setIsNavigatorGeoLocationInProgress] = useState(false);
  const [navigatorGeoLocationError, setNavigatorGeoLocationError] = useState(null);

  const [isCoordinatesToCountryConversionCompleted, setIsCoordinatesToCountryConversionCompleted] = useState(false);
  const [convertedCountry, setConvertedCountry] = useState(null);
  const [isCoordinatesToCountryConversionRequested, setIsCoordinatesToCountryConversionRequested] = useState(false);
  const [isCoordinatesToCountryConversionInitiated, setIsCoordinatesToCountryConversionInitiated] = useState(false);
  const [isCoordinatesToCountryConversionInProgress, setIsCoordinatesToCountryConversionInProgress] = useState(false);
  const [coordinatesToCountryConversionError, setCoordinatesToCountryConversionError] = useState(null);

  useEffect(
    () => {
      async function googleLocation() {
        console.log('initiate google geo loc');
        setIsGoogleGeoLocationInitiated(true);
        const googleCoordinates = await getGoogleGeoLocation();
        if(!googleCoordinates.hasError) {
          console.log('google geo loc was successful...');
          setGoogleGeoLocationError(null);
          setGoogleGeoLocation(previousValue => { return { coordinates: googleCoordinates.coordinates }});
          setIsGoogleGeoLocationInProgress( false);
          setIsGoogleGeoLocationCompleted(true);
          setIsCoordinatesToCountryConversionRequested(true);
        }
        else {
          console.log('google geo loc failed...');
          setGoogleGeoLocationError(googleCoordinates.errorMessage);
          setGoogleGeoLocation(null);
          setIsGoogleGeoLocationInProgress(false);
          setIsGoogleGeoLocationCompleted(true);
          setIsNavigatorGeoLocationRequested(true);
        }
      }
      if (isCountryAlreadySelected) {
        console.log('Country already selected... bypassing geolocation...');
        setIsUserGeoLocationInProgress(false);
        setIsUserGeoLocationCompleted(true);
      }
      else if(
        areFiltersLoaded && !filterLoadingError &&
        !isGoogleGeoLocationInitiated &&
        !isGoogleGeoLocationCompleted &&
        !isUserGeoLocationCompleted
      ) {
        setIsGoogleGeoLocationInProgress(true);
        setIsUserGeoLocationInProgress(true);
        googleLocation();
      }
    },
    [
      areFiltersLoaded,
      filterLoadingError,
      isGoogleGeoLocationInitiated,
      isGoogleGeoLocationCompleted,
      isUserGeoLocationCompleted,
      isCountryAlreadySelected
    ]
  )

  useEffect(
    () => {

      function onNavigatorGeoLocationSuccess(location) {
        setNavigatorGeoLocationError(null);
        setNavigatorGeoLocation(
          {
            coordinates: {
              latitude: location.coords.latitude,
              longitude: location.coords.longitude
            }
          }
        );
        setIsNavigatorGeoLocationInProgress(false);
        setIsNavigatorGeoLocationCompleted(true);
        setIsCoordinatesToCountryConversionRequested(true);
        console.log('nav geo loc successfully completed...');
      }

      function onNavigatorGeoLocationError(error) {
        setNavigatorGeoLocationError(error.message);
        console.warn(error.message);
        setNavigatorGeoLocation(null);
        setIsNavigatorGeoLocationInProgress(false);
        setIsNavigatorGeoLocationCompleted(true);
        setIsUserGeoLocationInProgress(false);
        setIsUserGeoLocationCompleted(true);
      }

      if(
        isNavigatorGeoLocationRequested &&
        !isNavigatorGeoLocationInitiated &&
        !isNavigatorGeoLocationCompleted
      ) {
        console.log('nav geo loc initiated...');
        setIsNavigatorGeoLocationInitiated(true);
        setIsNavigatorGeoLocationInProgress(true);
        if (!("geolocation" in navigator)) {
          onNavigatorGeoLocationError({
            code: 0,
            message: "Geolocation not supported",
          });
        }
        else {
          navigator.geolocation.getCurrentPosition(
            onNavigatorGeoLocationSuccess,
            onNavigatorGeoLocationError,
            appConstants.NAVIGATOR_GEOLOCATION_SETTINGS
          );
        }
      }
    },
    [
      isNavigatorGeoLocationRequested,
      isNavigatorGeoLocationInitiated,
      isNavigatorGeoLocationCompleted
    ]
  )

  // Convert whatever coordinates found into a possible Country name.
  useEffect(
    () => {
      function selectConvertedCountry(countryName) {
        console.log('country to filter criteria conversion triggered for ...', countryName);
        const filterValue = countryFilterList
          .filter(
            (countryFilterListItem) =>
              countryFilterListItem[appConstants.KEY_CODE].toLowerCase().trim() === countryName.toLowerCase().trim());
        if(!!filterValue && !!filterValue.length) {
          let selectedCountryFilterValue = {...filterValue[0]};
          selectCountryFilterValue(selectedCountryFilterValue);
        }
      }


      async function convertCoordinatesToCountry(coordinates) {
        console.log('geo location to country conversion initiated...');
        setIsCoordinatesToCountryConversionInitiated(true);
        const userCountryNames = await fetchUserCountry(coordinates.latitude, coordinates.longitude);
        if (!userCountryNames.hasError) {
          selectConvertedCountry(userCountryNames.countryNames.longName);
          setCoordinatesToCountryConversionError(null);
          setIsCoordinatesToCountryConversionInProgress(false);
          setIsCoordinatesToCountryConversionCompleted(true);
        }
        else {
          setCoordinatesToCountryConversionError(userCountryNames.errorMessage);
          setIsCoordinatesToCountryConversionInProgress(false);
          setIsCoordinatesToCountryConversionCompleted(true);
        }
        setIsUserGeoLocationInProgress(false);
        setIsUserGeoLocationCompleted(true);
      }
      if(
        isCoordinatesToCountryConversionRequested &&
        !isCoordinatesToCountryConversionInitiated &&
        !isCoordinatesToCountryConversionCompleted
      ) {
        setIsCoordinatesToCountryConversionInProgress(true);
        let coordinates;
        if(isGoogleGeoLocationCompleted && !googleGeoLocationError) {
          coordinates = googleGeoLocation.coordinates;
          convertCoordinatesToCountry(coordinates);
        }
        else if(isNavigatorGeoLocationCompleted && !navigatorGeoLocationError) {
          coordinates = navigatorGeoLocation.coordinates;
          convertCoordinatesToCountry(coordinates);
        }
        else {
          console.warn('User device coordinates could not be obtained.');
          setIsUserGeoLocationInProgress(false);
          setIsUserGeoLocationCompleted(true);
        }
      }
    },
    [
      isCoordinatesToCountryConversionRequested,
      isCoordinatesToCountryConversionInitiated,
      isCoordinatesToCountryConversionCompleted
    ]
  );

  return {
    isUserGeoLocationInProgress,
    isUserGeoLocationCompleted
  };
}
